const SENSITIVE_KEYS = [
  'email',
  'phone',
  'tel',
  'address',
  'street',
  'city',
  'state',
  'postcode',
  'zip',
  'notes',
  'contact',
  'first_name',
  'last_name',
];

const MASKED_VALUE = '[REDACTED]';

const getFormattedTimestamp = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Meses empiezan en 0
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const milliseconds = String(now.getMilliseconds()).padStart(3, '0'); // Milisegundos con tres dígitos

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
};

const maskEmail = (value: string): string => {
  const [local, domain] = value.split('@');
  if (!domain) {
    return value;
  }
  if (local.length <= 2) {
    return `${local[0] ?? ''}***@${domain}`;
  }
  return `${local[0]}***${local[local.length - 1]}@${domain}`;
};

const maskPhone = (value: string): string => {
  const digits = value.replace(/\D+/g, '');
  if (digits.length < 4) {
    return '***';
  }
  const maskedDigits = `${'*'.repeat(Math.max(digits.length - 2, 0))}${digits.slice(-2)}`;
  let idx = 0;
  return value.replace(/\d/g, () => maskedDigits[idx++] ?? '*');
};

const isLikelyEmail = (value: string): boolean => /.+@.+\..+/.test(value);

const isLikelyPhone = (value: string): boolean => value.replace(/\D+/g, '').length >= 8;

const sanitizeString = (value: string): string => {
  if (isLikelyEmail(value)) {
    return maskEmail(value);
  }
  if (isLikelyPhone(value)) {
    return maskPhone(value);
  }
  return value;
};

const sanitizeValue = (value: unknown, seen: WeakSet<object>): unknown => {
  if (typeof value === 'string') {
    return sanitizeString(value);
  }
  if (value === null || value === undefined) {
    return value;
  }
  if (typeof value !== 'object') {
    return value;
  }
  if (seen.has(value as object)) {
    return '[Circular]';
  }
  seen.add(value as object);

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item, seen));
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  const entries = Object.entries(value as Record<string, unknown>);
  const sanitized: Record<string, unknown> = {};

  entries.forEach(([key, entryValue]) => {
    const lower = key.toLowerCase();
    const containsSensitiveKey = SENSITIVE_KEYS.some((keyword) => lower.includes(keyword));

    if (containsSensitiveKey) {
      if (typeof entryValue === 'string') {
        sanitized[key] = sanitizeString(entryValue);
      } else if (entryValue === null || entryValue === undefined) {
        sanitized[key] = entryValue;
      } else if (Array.isArray(entryValue)) {
        sanitized[key] = entryValue.map((item) => sanitizeValue(item, seen));
      } else if (typeof entryValue === 'object') {
        sanitized[key] = MASKED_VALUE;
      } else {
        sanitized[key] = MASKED_VALUE;
      }
      return;
    }

    sanitized[key] = sanitizeValue(entryValue, seen);
  });

  return sanitized;
};

const sanitizeLogArgs = (args: unknown[]): unknown[] =>
  args.map((arg) => sanitizeValue(arg, new WeakSet<object>()));

/**
 * Extracts the caller's file and line number from the stack trace.
 * Skips internal LogsHandler frames to find the actual caller.
 * Returns format: "filename.tsx:lineNumber"
 */
const getCallerLocation = (): string => {
  const err = new Error();
  const stack = err.stack || '';
  const stackLines = stack.split('\n');

  // Find the first line that is NOT from LogsHandler.tsx (skip Error line and internal calls)
  for (let i = 1; i < stackLines.length; i++) {
    const line = stackLines[i];
    // Skip lines that contain LogsHandler
    if (line.includes('LogsHandler')) {
      continue;
    }

    // Try to extract file:line from various stack trace formats
    // Format 1 (Chrome/V8): "at functionName (file:///path/to/file.tsx:123:45)"
    // Format 2 (Chrome anonymous): "at file:///path/to/file.tsx:123:45"
    // Format 3 (Firefox): "functionName@file:///path/to/file.tsx:123:45"

    // Match the last path segment (filename) with line number
    const match = line.match(/([^/\\]+\.[jt]sx?):(\d+)(?::\d+)?/);
    if (match) {
      return `${match[1]}:${match[2]}`;
    }
  }

  return 'unknown';
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const logInfo = (...args: any[]) => {
  if (import.meta.env.VITE_REACT_ACTIVE_LOG) {
    const caller = getCallerLocation();
    console.log(`[INFO] [${getFormattedTimestamp()}] [${caller}]:`, ...sanitizeLogArgs(args));
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const logDebug = (...args: any[]) => {
  if (import.meta.env.VITE_REACT_ACTIVE_LOG) {
    const caller = getCallerLocation();
    console.debug(`[DEBUG] [${getFormattedTimestamp()}] [${caller}]:`, ...sanitizeLogArgs(args));
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const logError = (...args: any[]) => {
  if (import.meta.env.VITE_REACT_ACTIVE_LOG) {
    const caller = getCallerLocation();
    console.error(`[ERROR] [${getFormattedTimestamp()}] [${caller}]:`, ...sanitizeLogArgs(args));
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const logWarning = (...args: any[]) => {
  if (import.meta.env.VITE_REACT_ACTIVE_LOG) {
    const caller = getCallerLocation();
    console.warn(`[WARNING] [${getFormattedTimestamp()}] [${caller}]:`, ...sanitizeLogArgs(args));
  }
};

export { logDebug, logInfo, logWarning, logError };
export const __logsHandlerTestUtils = {
  maskEmail,
  maskPhone,
  sanitizeLogArgs,
};
